/** Shared GST / other-tax helpers. 0% values leave current billing totals unchanged. */

export const OTHER_TAX_1_TOOLTIP =
  "Hotels and restaurants often add a Service Charge (usually 5–10%) on the food/subtotal. Use this for that kind of bill-level charge.";

export const OTHER_TAX_2_TOOLTIP =
  "Examples: municipal / local body tax, tourism cess, packaging charge, or a convenience fee. Leave 0% if this bill has none.";

export const OTHER_TAX_EXAMPLES =
  "Common other taxes: Service Charge (hotels), Packaging Charge, Municipal Tax, Tourism Cess, Convenience Fee.";

export const EMPTY_OTHER_TAXES = {
  tax1: { enabled: false, label: "Service Charge", percent: 0 },
  tax2: { enabled: false, label: "Municipal Tax", percent: 0 },
};

export const roundMoney = (value) =>
  Math.round((Number(value) || 0) * 100) / 100;

export const parseGstPercent = (service = {}) => {
  const direct = service.gst_percent ?? service.gstPercent;
  const fromDirect = Number.parseFloat(direct);
  if (Number.isFinite(fromDirect) && fromDirect >= 0) return fromDirect;

  const keywords = Array.isArray(service.keywords)
    ? service.keywords
    : typeof service.keywords === "string"
      ? service.keywords.split(",")
      : [];
  for (const raw of keywords) {
    const text = String(raw || "").trim();
    if (text.toLowerCase().startsWith("gst:")) {
      const parsed = Number.parseFloat(text.slice(4).trim());
      if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    }
  }
  return 0;
};

export const gstAmountFromPercent = (price, gstPercent) => {
  const p = Math.max(0, Number(price) || 0);
  const g = Math.min(100, Math.max(0, Number(gstPercent) || 0));
  return roundMoney(p * (g / 100));
};

export const otherTaxAmountFromPercent = (base, percent) => {
  const b = Math.max(0, Number(base) || 0);
  const p = Math.min(100, Math.max(0, Number(percent) || 0));
  return roundMoney(b * (p / 100));
};

const normalizeSlot = (raw, fallbackLabel) => {
  const source = raw && typeof raw === "object" ? raw : {};
  const percent = Math.min(100, Math.max(0, Number(source.percent) || 0));
  return {
    enabled: source.enabled === true || percent > 0,
    label: String(source.label || fallbackLabel).trim() || fallbackLabel,
    percent,
  };
};

export const normalizeOtherTaxes = (raw) => {
  const source =
    raw?.data && typeof raw.data === "object"
      ? raw.data
      : raw?.other_taxes && typeof raw.other_taxes === "object"
        ? raw.other_taxes
        : raw && typeof raw === "object"
          ? raw
          : {};
  if (Array.isArray(source.taxes) && source.taxes.length) {
    const taxes = source.taxes.slice(0, 8).map((slot, i) =>
      normalizeSlot(
        slot,
        i === 0 ? "Service Charge" : i === 1 ? "Municipal Tax" : `Other tax ${i + 1}`,
      ),
    );
    return {
      tax1: taxes[0] || normalizeSlot(null, "Service Charge"),
      tax2: taxes[1] || normalizeSlot(null, "Municipal Tax"),
      extras: taxes.slice(2),
      taxes,
    };
  }
  return {
    tax1: normalizeSlot(source.tax1, "Service Charge"),
    tax2: normalizeSlot(source.tax2, "Municipal Tax"),
    extras: [],
    taxes: [
      normalizeSlot(source.tax1, "Service Charge"),
      normalizeSlot(source.tax2, "Municipal Tax"),
    ],
  };
};

export const buildOtherTaxLines = (taxableBase, otherTaxes) => {
  const normalized = normalizeOtherTaxes(otherTaxes);
  const lines = [];
  const slots = Array.isArray(normalized.taxes) && normalized.taxes.length
    ? normalized.taxes
    : [normalized.tax1, normalized.tax2, ...(normalized.extras || [])];
  for (const slot of slots) {
    if (!slot?.enabled || !(Number(slot.percent) > 0)) continue;
    lines.push({
      label: slot.label || "Other tax",
      percent: slot.percent,
      amount: otherTaxAmountFromPercent(taxableBase, slot.percent),
    });
  }
  return lines;
};

export const sumOtherTaxAmounts = (lines = []) =>
  roundMoney(lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0));
